import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { translations } from "@/i18n/translations";
import { toast } from "sonner";
import { Check, Loader2, PartyPopper, ShieldCheck } from "lucide-react";

// Canonical English values submitted to backend (admin stays in English).
const PROCEDURES = translations.en.quote.procedures;
const CONTACT_METHODS = translations.en.quote.contactMethods;
const BEST_TIMES = translations.en.quote.bestTimes;

const empty = { name: "", email: "", phone: "", procedure: "", best_contact: "Phone call", best_time: "Anytime / not sure", message: "", clinic_id: null, clinic_name: null, type: "quote" };

export default function QuoteDialog({ open, onOpenChange, prefill }) {
  const { t } = useLang();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const procLabels = t("quote.procedures");
  const contactLabels = t("quote.contactMethods");
  const timeLabels = t("quote.bestTimes");
  const label = (canon, list, labels) => { const i = list.indexOf(canon); return i === -1 ? canon : labels[i]; };

  useEffect(() => {
    if (open) {
      setForm({ ...empty, ...prefill });
      setDone(false);
    }
  }, [open, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit = form.name.trim() && /\S+@\S+\.\S+/.test(form.email);

  const submit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success(t("quote.successToast"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.errorToast"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="quote-dialog">
        {done ? (
          <div className="py-8 text-center flex flex-col items-center" data-testid="quote-success">
            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
              <PartyPopper className="w-8 h-8 text-accent" />
            </div>
            <DialogTitle className="text-2xl font-heading font-bold">{t("quote.allSet", { name: form.name.split(" ")[0] })}</DialogTitle>
            <p className="text-muted-foreground mt-2 max-w-sm">{t("quote.successBody", { email: form.email })}</p>            <Button className="mt-6 rounded-full" onClick={() => onOpenChange(false)} data-testid="quote-close-btn">{t("quote.done")}</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {t("quote.title")}{form.clinic_name ? ` · ${form.clinic_name}` : ""}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground -mt-1">
              {t("quote.intro")}
            </p>

            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>{t("quote.treatmentLabel")}</Label>
                <Select value={form.procedure} onValueChange={(v) => set("procedure", v)}>
                  <SelectTrigger className="h-12" data-testid="quote-procedure-select"><SelectValue placeholder={t("quote.selectProcedure")} /></SelectTrigger>
                  <SelectContent>{PROCEDURES.map((p) => <SelectItem key={p} value={p}>{label(p, PROCEDURES, procLabels)}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("quote.fullName")}</Label>
                  <Input className="h-12" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" data-testid="quote-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>{t("quote.phone")}</Label>
                  <Input className="h-12" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 555 000 0000" data-testid="quote-phone-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("quote.email")}</Label>
                <Input className="h-12" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@email.com" data-testid="quote-email-input" />
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("quote.bestContact")}</Label>
                  <Select value={form.best_contact} onValueChange={(v) => set("best_contact", v)}>
                    <SelectTrigger className="h-12" data-testid="quote-contact-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CONTACT_METHODS.map((m) => <SelectItem key={m} value={m}>{label(m, CONTACT_METHODS, contactLabels)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("quote.bestTime")}</Label>
                  <Select value={form.best_time} onValueChange={(v) => set("best_time", v)}>
                    <SelectTrigger className="h-12" data-testid="quote-besttime-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{BEST_TIMES.map((m) => <SelectItem key={m} value={m}>{label(m, BEST_TIMES, timeLabels)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("quote.messageLabel")} <span className="text-muted-foreground">{t("quote.optional")}</span></Label>
                <Textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={t("quote.messagePlaceholder")} data-testid="quote-message-input" />
              </div>
            </div>

            <Button className="w-full rounded-full h-12 mt-2" disabled={loading || !canSubmit} onClick={submit} data-testid="quote-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {t("quote.submit")}
            </Button>
            <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="w-3.5 h-3.5 text-accent" /> {t("quote.footnote")}
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
